/**
 * /admin/leads/add — single-lead intake form.
 *
 * Validates client-side (segment + context-min-50) so the operator
 * sees inline errors as they type, then re-validates server-side
 * via the IsSuperuser-gated /api/admin/v2/leads/ POST. On success
 * navigates to the import page so they can immediately bulk-upload
 * more.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, Loader2, Save } from "lucide-react";

import { createLead } from "../../../api/leadEngine.api";

const SEGMENTS = [
  { value: "league",      label: "League" },
  { value: "association", label: "Association" },
  { value: "franchise",   label: "Franchise" },
  { value: "broadcaster", label: "Broadcaster" },
  { value: "analyst_b2b", label: "Analyst (B2B)" },
  { value: "scorer",      label: "Scorer" },
  { value: "academy",     label: "Academy" },
  { value: "media",       label: "Media" },
  { value: "other",       label: "Other" },
];

const CONTEXT_MIN = 50;

export default function AddLead() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", org: "", role: "",
    segment: "league", context: "",
  });
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Client-side mirror of the server's _validate_row. The errors
  // panel surfaces these inline before the round-trip.
  function clientErrors() {
    const errs = [];
    if (!form.name.trim()) errs.push("name is required");
    const email = form.email.trim();
    if (!email) errs.push("email is required");
    else if (!email.includes("@") || !email.split("@")[1]?.includes(".")) {
      errs.push("email is not a valid address");
    }
    if (!form.segment) errs.push("segment is required");
    const ctx = form.context.trim();
    if (!ctx) errs.push("context is required");
    else if (ctx.length < CONTEXT_MIN) {
      errs.push(`context must be at least ${CONTEXT_MIN} characters — currently ${ctx.length}`);
    }
    return errs;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setFlash(null);
    const errs = clientErrors();
    if (errs.length) {
      setFlash({ ok: false, msg: errs.join(" · ") });
      return;
    }
    setBusy(true);
    try {
      const { data } = await createLead(form);
      setFlash({ ok: true, msg: `Saved "${data.name}" — state ${data.state}.` });
      // Reset for the next entry
      setForm({
        name: "", email: "", phone: "", org: "", role: "",
        segment: "league", context: "",
      });
    } catch (err) {
      const detail = err.response?.data;
      let msg;
      if (detail && typeof detail === "object") {
        if (detail.detail) {
          msg = Array.isArray(detail.detail) ? detail.detail.join(" · ") : detail.detail;
        } else {
          msg = JSON.stringify(detail);
        }
      } else {
        msg = err.message;
      }
      setFlash({ ok: false, msg });
    } finally {
      setBusy(false);
    }
  }

  const ctxLen = form.context.trim().length;
  const ctxOk = ctxLen >= CONTEXT_MIN;

  return (
    <div className="p-6 max-w-3xl">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Add lead</h1>
        <p className="text-sm text-text-secondary mt-1">
          Feed one prospect into the AI outreach pipeline. Bulk uploads:{" "}
          <Link to="/admin/leads/import" className="text-button-primary hover:underline">CSV import →</Link>
        </p>
      </header>

      {flash && (
        <div className={`mb-4 rounded-md border p-3 text-sm flex items-start gap-2 ${
          flash.ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-rose-500/40 bg-rose-500/10 text-rose-300"
        }`}>
          {flash.ok
            ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="min-w-0 whitespace-pre-wrap">{flash.msg}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 bg-bg-primary border border-border-default rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name *">
            <input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Sandeep Verma"
              className={inputCls}
            />
          </Field>
          <Field label="Email *">
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="sandeep@example.com"
              className={inputCls}
            />
          </Field>
          <Field label="Phone">
            <input
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+91 90000 00000"
              className={inputCls}
            />
          </Field>
          <Field label="Organisation">
            <input
              value={form.org}
              onChange={(e) => update("org", e.target.value)}
              placeholder="MAS Sports"
              className={inputCls}
            />
          </Field>
          <Field label="Role / title">
            <input
              value={form.role}
              onChange={(e) => update("role", e.target.value)}
              placeholder="Tournament Director"
              className={inputCls}
            />
          </Field>
          <Field label="Segment *">
            <select
              value={form.segment}
              onChange={(e) => update("segment", e.target.value)}
              className={inputCls}
            >
              {SEGMENTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={`Context * (drives outreach personalization — min ${CONTEXT_MIN} chars)`}>
          <textarea
            value={form.context}
            onChange={(e) => update("context", e.target.value)}
            rows={6}
            placeholder="How we know them, why they're a fit, referral source, what they're trying to solve. Be specific — this is what the AI uses to personalize the first email."
            className={`${inputCls} font-sans`}
          />
          <div className={`text-xs mt-1 ${ctxOk ? "text-emerald-400" : "text-text-secondary"}`}>
            {ctxLen} / {CONTEXT_MIN}
            {ctxOk ? " — ready" : ` — ${CONTEXT_MIN - ctxLen} more required`}
          </div>
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Link
            to="/admin/leads"
            className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-sm"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-button-primary text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 hover:brightness-110"
          >
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
              : <><Save className="w-3.5 h-3.5" /> Save lead</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-secondary focus:outline-none focus:border-button-primary";
